import React, {
  useRef,
  useEffect,
  useCallback,
  useMemo,
  useState,
  useImperativeHandle,
  memo,
} from "react";
import { Container, Graphics } from "@pixi/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";

import { useFreeSpinControl } from "../../../../Contexts/FreeSpinControlContext";
import { SOUND_NAME } from "../../../../sounds";
import anime from "animejs/lib/anime.es.js";
import FreeSpinOutroDialogController from "./FreeSpinOutroDialogController";
import { safeInit, formatToCustomLocale } from "../../../../utilities";
import { selectShowFreeSpinOutro } from "../../../../State/Slices/animationsSlice";
import { setFreeSpinCounterVisible } from "../../../../State/actions";
import { selectFreeSpinTotal } from "../../../../State/Slices/featureGameSettingsSlice";
import { soundManager } from "../../../../SoundManager";

// Overlay settings (matching intro dialog exactly)
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_BACKGROUND_ALPHA = 0.85;
const OVERLAY_FADE_DURATION = 350;

const FreeSpinOutroDialog = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const showFreeSpinOutro = useSelector(selectShowFreeSpinOutro);
  const freeSpinTotal = useSelector(selectFreeSpinTotal);
  const { exitFreeSpinReveal } = useFreeSpinControl();

  const outroControllerRef = useRef(null);
  const outroContainerRef = useRef(null);
  const overlayRef = useRef(null);
  const buttonRef = useRef(null);
  const prevShowOutroRef = useRef(false);
  const dimensionsContext = useDimensions();
  const {
    dimensions,
    uiSymbolHeight: uiSymbolSize,
    device,
  } = dimensionsContext;


  // Overlay state (matching intro pattern)
  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const overlayAnimRef = useRef(null);

  // Expose fadeInBackground / fadeOutBackground via overlayRef (same API as intro)
  useImperativeHandle(overlayRef, () => ({
    fadeInBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    fadeOutBackground(callback) {
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
      overlayAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          if (callback && typeof callback === "function") callback();
        },
      });
    },
    setAlpha(alpha) {
      setOverlayAlpha(alpha);
    },
  }));

  // Initialize controller once
  useEffect(() => {
    if (outroContainerRef.current && !outroControllerRef.current) {
      // Set button alpha to 0 for this instance
      if (buttonRef.current) {
        buttonRef.current.alpha = 0;
      }

      safeInit(() => {
        outroControllerRef.current = new FreeSpinOutroDialogController(
          outroContainerRef.current,
          dimensionsContext,
          dispatch,
          uiSymbolSize,
          overlayRef,
          buttonRef,
          t,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (outroControllerRef.current) {
      outroControllerRef.current.updateDimensions(
        dimensionsContext,
        uiSymbolSize,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions.width, dimensions.height, uiSymbolSize, device]);

  // Play animation when triggered
  useEffect(() => {
    if (outroControllerRef.current && showFreeSpinOutro) {
      outroControllerRef.current.updateTotalWinValue(
        formatToCustomLocale(freeSpinTotal || 0),
      );
      outroControllerRef.current.startSequence();
    }
  }, [showFreeSpinOutro, freeSpinTotal]);



  // When outro finishes (showFreeSpinOutro: true → false), exit free spin mode
  useEffect(() => {
    if (prevShowOutroRef.current && !showFreeSpinOutro) {
      // Hide counter (plays out animation)
      dispatch(setFreeSpinCounterVisible(false));
      // Deactivate free spins + reveal masks to uncover base game
      exitFreeSpinReveal();
    }
    prevShowOutroRef.current = showFreeSpinOutro;
  }, [showFreeSpinOutro, dispatch, exitFreeSpinReveal]);

  // Handle user click on overlay to close animation
  const handleBackgroundClick = useCallback(
    (evt) => {
      if (evt && typeof evt.preventDefault === "function") evt.preventDefault();
      if (evt && typeof evt.stopPropagation === "function")
        evt.stopPropagation();
      if (evt && typeof evt.stopImmediatePropagation === "function")
        evt.stopImmediatePropagation();

      if (outroControllerRef.current && showFreeSpinOutro) {
        const accepted = outroControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      }
    },
    [showFreeSpinOutro],
  );

  // Add spacebar handling
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " " && event.keyCode !== 32) {
        return;
      }

      // Ignore auto-repeat
      if (event.repeat) {
        event.preventDefault();
        if (typeof event.stopPropagation === "function")
          event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function")
          event.stopImmediatePropagation();
        return;
      }

      if (!showFreeSpinOutro || !outroControllerRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      try {
        const accepted = outroControllerRef.current.handleUserClick();
        if (accepted) {
          soundManager.play(SOUND_NAME.button_click);
        }
      } catch {
        if (
          outroControllerRef.current &&
          outroControllerRef.current.finishAnimation
        ) {
          outroControllerRef.current.finishAnimation();
        }
      }
    };

    if (showFreeSpinOutro) {
      document.addEventListener("keydown", handleKeyDown, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [showFreeSpinOutro, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (outroControllerRef.current) {
        outroControllerRef.current.cleanup();
      }
      if (overlayAnimRef.current) overlayAnimRef.current.pause();
    };
  }, []);

  // Memoize container position
  const containerPosition = useMemo(
    () => ({
      x: dimensions.width / 2,
      y: dimensions.height / 2,
    }),
    [dimensions.width, dimensions.height],
  );

  // Overlay draw function (matching intro)
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  return (
    <Container
      name="free_spin_outro"
      x={containerPosition.x}
      y={containerPosition.y}
      sortableChildren={true}
      zIndex={9}
      ref={outroContainerRef}
    >
      {/* Background overlay - inline Graphics (same as intro) */}
      <Graphics
        name="free_spin_outro_overlay"
        draw={drawOverlay}
        alpha={overlayAlpha}
        x={-containerPosition.x}
        y={-containerPosition.y}
        zIndex={0}
        eventMode={showFreeSpinOutro ? "static" : "none"}
        pointerdown={handleBackgroundClick}
        cursor={showFreeSpinOutro ? "pointer" : "default"}
      />

      {/* Spine animation and button managed internally by FreeSpinOutroDialogController */}
    </Container>
  );
};

export default memo(FreeSpinOutroDialog);
